import { type ReactNode, useState, useMemo } from "react";
import { Table } from "react-bootstrap";

import NoDataFound from "../common/NoDataFound";

type DataRow = Record<string, unknown>;

type SortDirection = "asc" | "desc";

type DashboardTableProps = {
  isReportLoading: boolean;
  reportHeader: string[];
  reportData: DataRow[];
  responsive?: boolean;
};

export default function DashboardTable({
  isReportLoading,
  reportHeader,
  reportData,
  responsive = true,
}: DashboardTableProps) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const sortedData = useMemo(() => {
    if (!sortColumn) return reportData;
    return [...reportData].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal), undefined, {
              numeric: true,
              sensitivity: "base",
            });
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [reportData, sortColumn, sortDirection]);

  function handleHeaderClick(column: string) {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  return (
    <Table striped hover responsive={responsive}>
      <thead className="sticky-top" style={{ zIndex: 2 }}>
        <tr className="text-center">
          {reportHeader.map((column, index) => (
            <th
              style={{
                whiteSpace: "nowrap",
                cursor: "pointer",
                userSelect: "none",
              }}
              key={index}
              onClick={() => handleHeaderClick(column)}
            >
              {column}
              {sortColumn === column ? (
                <i
                  className={`bi bi-sort-${sortDirection === "asc" ? "up" : "down"} ms-1`}
                />
              ) : (
                <i className="bi bi-arrow-down-up ms-1 text-muted opacity-50" />
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isReportLoading ? (
          [...Array(5).keys()].map((_, index) => (
            <tr className="table-row" key={index}>
              {reportHeader.map((column) => (
                <td key={column} className="placeholder-glow">
                  <span className="placeholder placeholder-sm w-100"></span>
                </td>
              ))}
            </tr>
          ))
        ) : sortedData.length > 0 ? (
          sortedData.map((row, index: number) => (
            <tr
              className="table-row"
              key={(row.id as string | number | undefined) ?? index}
              style={{
                fontWeight: row["Name"] === "Total" ? "bold" : "normal",
              }}
            >
              {reportHeader.map((column) => (
                <td key={column} className="text-center">
                  {typeof row[column] === "string" ||
                  typeof row[column] === "number"
                    ? (row[column] as ReactNode)
                    : "-"}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td className="bg-white border-0">
              <NoDataFound />
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  );
}
